import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { localDateOf } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { dayLabel, timeInTz } from '../../lib/format';
import { useOrgClock } from '../../lib/useOrgClock';
import { SectionCard } from '../ui/Detail';

/** Lines shown before "Show all": the latest decisions first, the rest one tap away. */
const FIRST = 10;

/**
 * The billing log (Inventory v2 12d, decision 8, prototype v3): every price change, discount,
 * write-off and charge on the job, with who and when, newest first. The server writes each line
 * (`partsBilling.log`); this card only lists them. Nothing to show draws nothing.
 */
export function BillingLogCard({ jobId }: { jobId: string }) {
  const trpc = useTRPC();
  const log = useQuery(trpc.partsBilling.log.queryOptions({ jobId }));
  const { tz } = useOrgClock();
  const [all, setAll] = useState(false);
  if (log.isError) {
    return (
      <SectionCard title="Billing log">
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn't load this job's billing log.{' '}
          <button type="button" className="underline" onClick={() => void log.refetch()}>
            Try again
          </button>
        </p>
      </SectionCard>
    );
  }
  const entries = log.data?.entries ?? [];
  if (entries.length === 0) return null;
  const shown = all ? entries : entries.slice(0, FIRST);
  return (
    <SectionCard title="Billing log">
      {/* In the body, not the header row: at phone width it would squeeze the title. */}
      <p className="text-xs text-muted-foreground">
        Prices, discounts, write-offs and charges decided on this job, with who and when.
      </p>
      <ol className="divide-y divide-border text-sm">
        {shown.map((e) => (
          <li key={e.id} className="py-2">
            <p>{e.text}</p>
            <p className="text-xs text-muted-foreground">
              {[e.who, tz ? whenIn(e.at, tz) : null].filter(Boolean).join(' · ')}
            </p>
          </li>
        ))}
      </ol>
      {all && log.data?.truncated ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Older decisions on this job aren't shown.
        </p>
      ) : null}
      {entries.length > FIRST ? (
        <button
          type="button"
          className="mt-2 text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
          aria-expanded={all}
          onClick={() => setAll((a) => !a)}
        >
          {all
            ? 'Show the latest only'
            : log.data?.truncated
              ? `Show the latest ${entries.length}`
              : `Show all ${entries.length}`}
        </button>
      ) : null}
    </SectionCard>
  );
}

/** "Tue, Oct 6, 9:01 AM", the shop's day and time; a year that isn't this one is said. */
export function whenIn(at: Date | string, tz: string, now: Date = new Date()): string {
  const day = localDateOf(new Date(at), tz);
  const year =
    day.slice(0, 4) === localDateOf(now, tz).slice(0, 4) ? '' : `, ${day.slice(0, 4)}`;
  return `${dayLabel(day, tz)}${year}, ${timeInTz(at, tz)}`;
}
